import React, { useMemo, useState } from 'react';
import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useRouter } from 'next/router';
import EditorImg from 'lowcode/components/EditorImg';
import EditorLink from 'lowcode/components/EditorLink';
import { useStore } from '../context';
import css from './index.module.scss';
import { imgList } from './imgConfig';
import styledModal from '@/components/AppreciationDay/Nav/index.module.scss';
import { emailMap, euLocaleList } from '@/constants/appreciationDay';
import { Button } from '@/components/UI';
import EfModal from '@/components/UI/Modal';
import Mailchimp from '@/components/SignUpForm/Mailchimp';
import MailchimpView from '@/components/SignUpForm/Mailchimp/BaseFormView';
import { naLocaleList } from '@/constants/appreciationDay';
import Emarsys from '@/components/SignUpForm/Emarsys';
import { useLang } from '@/app/utils/useLang';
import gtm from '@/utils/gtm';
import getLocal from '@/app/utils/getLocal';
import { useMonthlyStore } from '@/components/EcocreditsMonthlyMadness/MonthlyProvider';
import { IPageStatus } from '@/components/EcocreditsMonthlyMadness/type';
import { getPrivacyPolicy } from '@/utils/common';

// interface AppreciationDayProps {
//     title: string;
//     subtitle: string;
// }

const AppreciationDay: React.FC = () => {
    const { t } = useLang('ecocredits');
    const router = useRouter();
    const { locale } = getLocal();
    const { FREE_PRODUCTS_RULES_URL: productRulesList } = useStore();
    const { pageStatus } = useMonthlyStore();
    const [open, setOpen] = useState(false);
    // const [showActiveRules, setShowActiveRules] = useState(false);
    const [agree, setAgree] = useState(false);
    const isNa = naLocaleList.includes(locale);
    const isEu = euLocaleList.includes(locale);

    const img = imgList[locale] || imgList.eu;

    const handleNextPage = () => {
        if (['au', 'za'].includes(locale)) {
            router.push(`/${locale}/ecocredits-appreciation-week`);
            return;
        }

        if (locale === 'za') {
            router.push('https://za.ecoflow.com/pages/ecocredits-appreciation-week');
            return;
        }

        if (isEu) {
            router.push(`https://${locale}.ecoflow.com/pages/ecocredits-monthly-madness`);
            return;
        }

        if (isNa) {
            router.push(`https://www.ecoflow.com/${locale}/ecocredits-monthly-madness?ref=ecocreditspage`);
            return;
        }

        // if (isEu || isNa || isZa) {
        //     router.push(`/${locale}/members-day`);
        //     return;
        // }
        router.push(`/${locale}/ecocredits-monthly-madness`);
    };

    const successCallBack = () => {
        setOpen(true);
        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'activity_submit',
                button_name: t('appreciationday_btn3'),
                status: 'success',
            });
        } catch (e) {
            console.error(e);
        }
    };

    const errorCallback = () => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_submit',
            button_name: t('appreciationday_btn3'),
            status: 'fail',
        });
    };
    const emailConf = emailMap[locale] || emailMap.us;
    const createdEmail = () => {
        if (emailConf.type === 'mailchimp') {
            return (
                <>
                    <Mailchimp successCallback={successCallBack} errorCallback={errorCallback}>
                        <MailchimpView
                            action={emailConf.action}
                            extraFields={emailConf.extraFields}
                            submitText={t('appreciationday_btn3')}
                            placeholder={t('appreciationday_tips')}
                        />
                    </Mailchimp>
                    <Checkbox onChange={(e: CheckboxChangeEvent) => setAgree(e.target.checked)} checked={agree}>
                        <p className="protocol">
                            {t.rich('appreciationday_protocol', {
                                line1: (text: string) => (
                                    <EditorLink className="link" href={getPrivacyPolicy(locale)} target="_blank" rel="noreferrer">
                                        {text}
                                    </EditorLink>
                                ),
                                line2: (text: string) => (
                                    <EditorLink
                                        className="link"
                                        href={`https://${locale}.ecoflow.com/policies/terms-of-service`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {text}
                                    </EditorLink>
                                ),
                            })}
                        </p>
                    </Checkbox>
                </>
            );
        }
        return (
            <>
                <Emarsys
                    source={emailConf.source}
                    disabled={isEu && !agree}
                    submitText={t('appreciationday_btn3')}
                    placeholder={t('appreciationday_tips')}
                    successCallback={successCallBack}
                    errorCallback={errorCallback}
                />
                {isEu && (
                    <Checkbox
                        onChange={(e: CheckboxChangeEvent) => {
                            console.log(e);
                            setAgree(e.target.checked);
                        }}
                        checked={agree}
                        style={{ marginTop: '6px' }}
                    >
                        <p className="protocol">
                            {t.rich('appreciationday_protocol', {
                                line2: (text: string) => (
                                    <EditorLink
                                        className="link"
                                        href={`https://${locale}.ecoflow.com/policies/terms-of-service`}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {text}
                                    </EditorLink>
                                ),
                                line1: (text: string) => (
                                    <EditorLink className="link" href={getPrivacyPolicy(locale)} target="_blank" rel="noreferrer">
                                        {text}
                                    </EditorLink>
                                ),
                            })}
                        </p>
                    </Checkbox>
                )}
            </>
        );
    };
    const createdText = useMemo(() => {
        if (pageStatus === IPageStatus.NOTSTARTED) {
            return (
                <>
                    <p className="subtitle">{t('appreciationday_subtitle')}</p>
                    <h5 className="title">{t('appreciationday_title')}</h5>
                    <p className="desc">{t('appreciationday_desc')}</p>
                    <div className="email-box">
                        <p>{t('appreciationday_inptitle')}</p>
                        {createdEmail()}
                    </div>
                </>
            );
        }
        if (pageStatus === IPageStatus.TEASER) {
            return (
                <>
                    <p className="subtitle">{t('appreciationday_first_subtitle')}</p>
                    <h5 className="title">{t('appreciationday_title')}</h5>
                    <p className="desc">{t('appreciationday_first_desc')}</p>
                    <Button type="primary" className="default-btn btn" aria-label={'Click here'} onClick={handleNextPage}>
                        {t('appreciationday_btn')}
                    </Button>
                </>
            );
        }
        if (pageStatus === IPageStatus.ACTIVITY) {
            return (
                <>
                    <p className="subtitle">{t('appreciationday_second_subtitle')}</p>
                    <h5 className="title">{t('appreciationday_title')}</h5>
                    <p className="desc">{t('appreciationday_second_desc')}</p>
                    <Button type="primary" className="default-btn btn" aria-label={'Click here'} onClick={handleNextPage}>
                        {t('appreciationday_btn2')}
                    </Button>
                </>
            );
        }
        if (pageStatus === IPageStatus.END) {
            return (
                <>
                    <p className="subtitle">{t('appreciationday_subtitle1')}</p>
                    <h5 className="title">{t('appreciationday_title1')}</h5>
                    <p className="desc">{t('appreciationday_third_desc')}</p>
                    <div className="email-box">
                        <p className="email-box-header">{t('appreciationday_inptitle')}</p>
                        {createdEmail()}
                    </div>
                </>
            );
        }
    }, [pageStatus, agree]);

    return (
        <div className={`${css.appreciation_day} main-page`} id="APPRECIATION_DAY">
            <div className="banner">
                <EditorImg pc={img.pc} h5={img.h5} alt={t('appreciationday_title')} aria-label={t('appreciationday_title')} />
                <div className="text_box">{createdText}</div>
            </div>

            {/* 订阅弹窗 */}
            <EfModal
                className={`${styledModal.subscribe_modal}`}
                open={open}
                visible={open}
                width={812}
                onCancel={() => {
                    setOpen(false);
                }}
                zIndex={999}
            >
                <div className="content">
                    <h6>{t('appreciationday_success_title')}</h6>
                    <p>{t('appreciationday_success_desc')}</p>
                </div>
            </EfModal>
            {/* 临时配置的活动rules */}
            {/* <EfModal
                className={css.active_rules_modal}
                open={showActiveRules}
                visible={showActiveRules}
                zIndex={1200}
                onCancel={() => {
                    setShowActiveRules(false);
                }}
            >
                <div className="content">
                    <p className="title">{t('redeem_ecocredits_rules_title1')}</p>
                    <p>{t('redeem_ecocredits_rules_desc1')}</p>
                    <p className="title">{t('redeem_ecocredits_rules_title2')}</p>
                    <p>{t('redeem_ecocredits_rules_desc2')}</p>
                    <p>{t('redeem_ecocredits_rules_desc3')}</p>
                </div>
            </EfModal> */}
        </div>
    );
};

export default AppreciationDay;
